import React, { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Loader2, ShieldCheck, Upload, BadgeCheck, AlertCircle, FileText, MapPin } from "lucide-react";
import { toast } from "sonner";

const STATUS_CONFIG = {
  unverified: { label: "Not submitted", color: "bg-[#F3EFE9] text-[#5C5852]" },
  pending: { label: "Under review", color: "bg-[#D9923B]/15 text-[#D9923B]" },
  approved: { label: "Verified", color: "bg-[#4A5D23]/15 text-[#4A5D23]" },
  rejected: { label: "Rejected", color: "bg-[#B84A28]/15 text-[#B84A28]" },
};

const Verify = () => {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const idRef = useRef(null);
  const addrRef = useRef(null);

  const [info, setInfo] = useState(null);
  const [badgePrice, setBadgePrice] = useState(25);
  const [form, setForm] = useState({
    id_doc_path: "",
    address_doc_path: "",
    full_legal_name: user?.name || "",
    address_text: "",
  });
  const [uploading, setUploading] = useState({ id: false, addr: false });
  const [submitting, setSubmitting] = useState(false);
  const [badgeLoading, setBadgeLoading] = useState(false);
  const [badgeRedeeming, setBadgeRedeeming] = useState(false);
  const [credit, setCredit] = useState(0);

  useEffect(() => {
    api.get("/verification/me").then((r) => {
      setInfo(r.data);
      setForm((f) => ({
        ...f,
        id_doc_path: r.data.id_doc_path || "",
        address_doc_path: r.data.address_doc_path || "",
        full_legal_name: r.data.full_legal_name || f.full_legal_name,
        address_text: r.data.address_text || "",
      }));
    });
    api.get("/verification/badge-price").then((r) => setBadgePrice(r.data.price));
    if (user) api.get("/me/credit-balance?currency=GBP").then((r) => setCredit(r.data.balance || 0)).catch(() => {});
  }, []);

  const uploadDoc = async (file, kind) => {
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [`${kind === "id" ? "id_doc_path" : "address_doc_path"}`]: data.path }));
      toast.success(`${kind === "id" ? "ID document" : "Address proof"} uploaded`);
    } catch {
      toast.error("Upload failed");
    } finally { setUploading((u) => ({ ...u, [kind]: false })); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.id_doc_path || !form.address_doc_path) {
      toast.error("Please upload both your ID and proof of address");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/verification/submit", form);
      toast.success("Documents submitted! Admin will review within 24-48h.");
      const { data } = await api.get("/verification/me");
      setInfo(data);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  const buyBadge = async () => {
    setBadgeLoading(true);
    try {
      const { data } = await api.post("/payments/verified-badge", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBadgeLoading(false);
    }
  };

  const redeemBadgeWithCredits = async () => {
    setBadgeRedeeming(true);
    try {
      await api.post("/payments/redeem-credits", { target: "verified_badge" });
      toast.success("Verified badge activated using referral credits!");
      // Refresh local info so the badge shows immediately
      const me = await api.get("/auth/me");
      setInfo(me.data);
      setCredit(c => Math.max(0, c - badgePrice));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not redeem");
    } finally { setBadgeRedeeming(false); }
  };

  if (!user) return null;
  const status = info?.verification_status || user.verification_status || "unverified";
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.unverified;
  const isApproved = status === "approved";
  const isPending = status === "pending";

  return (
    <div className="container-narrow py-10 max-w-4xl">
      <div className="label-overline text-[#C05A3C] mb-2">Seller verification</div>
      <h1 className="font-display text-4xl mb-3">Get verified to start selling</h1>
      <p className="text-muted-foreground font-sans-ui max-w-2xl">
        AfriMarket reviews every seller manually to keep our diaspora community safe. Upload a
        government-issued ID and a recent proof of address. Most reviews complete within 24–48 hours.
      </p>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        {/* Status card */}
        <div className="lg:col-span-1 space-y-4">
          <Card className="p-6 bg-white border-black/10">
            <div className="label-overline text-muted-foreground mb-2">Your status</div>
            <Badge className={`${cfg.color} text-sm py-1 px-3`} data-testid="verify-status-badge">{cfg.label}</Badge>
            {info?.verification_notes && (
              <div className="mt-4 p-3 bg-[#F3EFE9] rounded-md text-sm">
                <div className="font-semibold mb-1 text-xs uppercase tracking-wider text-muted-foreground">Admin note</div>
                {info.verification_notes}
              </div>
            )}
            <ul className="mt-5 space-y-3 text-sm font-sans-ui">
              <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-[#2A3D36] mt-0.5"/> Listings unlock after approval</li>
              <li className="flex items-start gap-2"><BadgeCheck className="w-4 h-4 text-[#C05A3C] mt-0.5"/> Optional verified badge available</li>
              <li className="flex items-start gap-2"><AlertCircle className="w-4 h-4 text-muted-foreground mt-0.5"/> Documents are only visible to admins</li>
            </ul>
          </Card>

          {/* Verified badge upsell — only after KYC approval */}
          <Card className="p-6 bg-[#1A2024] text-white border-transparent">
            <BadgeCheck className="w-7 h-7 text-[#D9923B] mb-3"/>
            <div className="font-display text-2xl leading-tight">Verified badge</div>
            <p className="text-sm text-white/70 mt-2 leading-relaxed">
              Stand out with a ✓ on every listing. Lifts trust + conversion. One-time payment.
            </p>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-display text-3xl">£{badgePrice}</span>
              <span className="text-xs text-white/60">one-time</span>
            </div>
            {user.verified_badge ? (
              <Badge className="mt-4 bg-[#D9923B] text-[#1A2024]">You already have it</Badge>
            ) : (
              <>
                <Button
                  onClick={buyBadge}
                  disabled={!isApproved || badgeLoading}
                  className="mt-4 w-full bg-[#D9923B] hover:bg-[#D9923B]/90 text-[#1A2024] font-semibold rounded-md"
                  data-testid="buy-badge-btn"
                >
                  {badgeLoading ? <Loader2 className="w-4 h-4 animate-spin"/> : (isApproved ? "Get verified badge" : "Approve KYC first")}
                </Button>
                {isApproved && credit >= badgePrice && (
                  <Button
                    type="button"
                    onClick={redeemBadgeWithCredits}
                    disabled={badgeRedeeming}
                    variant="outline"
                    className="mt-2 w-full border-[#D9923B]/40 text-[#D9923B] hover:bg-[#D9923B]/10 hover:text-[#D9923B] rounded-md"
                    data-testid="badge-redeem-credits-btn"
                  >
                    {badgeRedeeming ? <Loader2 className="w-4 h-4 animate-spin"/> : <>Use £{credit.toFixed(2)} credits instead</>}
                  </Button>
                )}
              </>
            )}
          </Card>
        </div>

        {/* Form */}
        <div className="lg:col-span-2">
          {isApproved && !info?.id_doc_path ? null : (
            <Card className="p-6 sm:p-8 bg-white border-black/10">
              <h2 className="font-display text-2xl mb-1">Submit your documents</h2>
              <p className="text-sm text-muted-foreground mb-6">All fields required. Files: PDF, JPG or PNG, up to 8MB each.</p>

              <form onSubmit={submit} className="space-y-5" data-testid="verify-form">
                <div>
                  <Label className="label-overline">Full legal name</Label>
                  <Input
                    data-testid="verify-name"
                    value={form.full_legal_name}
                    onChange={(e) => setForm({ ...form, full_legal_name: e.target.value })}
                    placeholder="As shown on your ID"
                    className="mt-2"
                    disabled={isApproved || isPending}
                  />
                </div>
                <div>
                  <Label className="label-overline">Home address</Label>
                  <Textarea
                    data-testid="verify-address"
                    value={form.address_text}
                    onChange={(e) => setForm({ ...form, address_text: e.target.value })}
                    placeholder="Street, city, postal code, country"
                    rows={3}
                    className="mt-2"
                    disabled={isApproved || isPending}
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <DocUpload
                    label="Government ID"
                    sub="Passport, driver's licence or national ID"
                    icon={FileText}
                    file={form.id_doc_path}
                    uploading={uploading.id}
                    disabled={isApproved || isPending}
                    onPick={() => idRef.current?.click()}
                    testId="upload-id"
                  />
                  <DocUpload
                    label="Proof of address"
                    sub="Recent bank statement or utility bill"
                    icon={MapPin}
                    file={form.address_doc_path}
                    uploading={uploading.addr}
                    disabled={isApproved || isPending}
                    onPick={() => addrRef.current?.click()}
                    testId="upload-address"
                  />
                  <input ref={idRef} type="file" accept="image/*,application/pdf" hidden onChange={(e) => e.target.files?.[0] && uploadDoc(e.target.files[0], "id")} data-testid="file-id"/>
                  <input ref={addrRef} type="file" accept="image/*,application/pdf" hidden onChange={(e) => e.target.files?.[0] && uploadDoc(e.target.files[0], "addr")} data-testid="file-addr"/>
                </div>

                {!isApproved && !isPending && (
                  <Button type="submit" disabled={submitting} className="bg-[#C05A3C] hover:bg-[#D46B4E] rounded-md w-full sm:w-auto" data-testid="verify-submit">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <ShieldCheck className="w-4 h-4 mr-2"/>}
                    Submit for review
                  </Button>
                )}
                {isPending && (
                  <div className="text-sm text-[#D9923B] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/> Documents under admin review</div>
                )}
                {isApproved && (
                  <div className="text-sm text-[#4A5D23] flex items-center gap-2"><ShieldCheck className="w-4 h-4"/> You&apos;re verified! <Link to="/create" className="ml-1 underline text-[#C05A3C]">Create your first listing →</Link></div>
                )}
              </form>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

const DocUpload = ({ label, sub, icon: Icon, file, uploading, disabled, onPick, testId }) => (
  <button
    type="button"
    onClick={onPick}
    disabled={disabled || uploading}
    data-testid={testId}
    className={`text-left p-5 rounded-md border-2 border-dashed ${file ? "border-[#4A5D23] bg-[#4A5D23]/5" : "border-black/15 hover:border-[#C05A3C]"} transition-colors disabled:opacity-60`}
  >
    <Icon className="w-5 h-5 mb-2 text-[#1A1A1A]"/>
    <div className="font-semibold text-sm">{label}</div>
    <div className="text-xs text-muted-foreground mt-1">{sub}</div>
    <div className="mt-3 text-xs font-semibold flex items-center gap-1">
      {uploading ? <><Loader2 className="w-3 h-3 animate-spin"/> Uploading…</> :
       file ? <><ShieldCheck className="w-3 h-3 text-[#4A5D23]"/> Uploaded</> :
       <><Upload className="w-3 h-3"/> Click to upload</>}
    </div>
  </button>
);

export default Verify;
